import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext.js';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { NotificationProvider } from './context/NotificationContext.js';

import { Sidebar } from './components/layout/Sidebar.js';
import { Header } from './components/layout/Header.js';
import { UniversalSearchModal } from './components/search/UniversalSearchModal.js';
import { NotificationDrawer } from './components/notifications/NotificationDrawer.js';
import { Skeleton } from './components/ui/Skeleton.js';
import { BrandedLoader } from './components/ui/BrandedLoader.js';
import ErrorBoundary from './components/ui/ErrorBoundary.js';

import './theme/global.css';

// Lazy-loaded route components for high performance code-splitting
const LoginPage = lazy(() => import('./pages/auth/LoginPage.js').then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js').then((m) => ({ default: m.NotFoundPage })));
const ForbiddenPage = lazy(() => import('./pages/ForbiddenPage.js').then((m) => ({ default: m.ForbiddenPage })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage.js').then((m) => ({ default: m.DashboardPage })));
const UsersPage = lazy(() => import('./pages/users/UsersPage.js').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/roles/RolesPage.js').then((m) => ({ default: m.RolesPage })));
const DepartmentsPage = lazy(() => import('./pages/departments/DepartmentsPage.js').then((m) => ({ default: m.DepartmentsPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage.js').then((m) => ({ default: m.SettingsPage })));
const AuditLogsPage = lazy(() => import('./pages/audit/AuditLogsPage.js').then((m) => ({ default: m.AuditLogsPage })));

const FinanceDashboardPage = lazy(() => import('./pages/finance/FinanceDashboardPage.js').then((m) => ({ default: m.FinanceDashboardPage })));
const ChartOfAccountsPage = lazy(() => import('./pages/finance/ChartOfAccountsPage.js').then((m) => ({ default: m.ChartOfAccountsPage })));
const TransactionsPage = lazy(() => import('./pages/finance/TransactionsPage.js').then((m) => ({ default: m.TransactionsPage })));
const InvoicesPage = lazy(() => import('./pages/finance/InvoicesPage.js').then((m) => ({ default: m.InvoicesPage })));
const ExpensesPage = lazy(() => import('./pages/finance/ExpensesPage.js').then((m) => ({ default: m.ExpensesPage })));
const BudgetsPage = lazy(() => import('./pages/finance/BudgetsPage.js').then((m) => ({ default: m.BudgetsPage })));
const OwnerInvestmentsPage = lazy(() => import('./pages/finance/OwnerInvestmentsPage.js').then((m) => ({ default: m.OwnerInvestmentsPage })));
const FinancialReportsPage = lazy(() => import('./pages/finance/FinancialReportsPage.js').then((m) => ({ default: m.FinancialReportsPage })));

const HrDashboardPage = lazy(() => import('./pages/hrms/HrDashboardPage.js').then((m) => ({ default: m.HrDashboardPage })));
const AttendancePage = lazy(() => import('./pages/hrms/AttendancePage.js').then((m) => ({ default: m.AttendancePage })));
const LeaveManagementPage = lazy(() => import('./pages/hrms/LeaveManagementPage.js').then((m) => ({ default: m.LeaveManagementPage })));
const DailyReportsPage = lazy(() => import('./pages/hrms/DailyReportsPage.js').then((m) => ({ default: m.DailyReportsPage })));
const TaskManagementPage = lazy(() => import('./pages/hrms/TaskManagementPage.js').then((m) => ({ default: m.TaskManagementPage })));
const PerformancePage = lazy(() => import('./pages/hrms/PerformancePage.js').then((m) => ({ default: m.PerformancePage })));
const AnnouncementsPage = lazy(() => import('./pages/hrms/AnnouncementsPage.js').then((m) => ({ default: m.AnnouncementsPage })));

const CrmDashboardPage = lazy(() => import('./pages/crm/CrmDashboardPage.js').then((m) => ({ default: m.CrmDashboardPage })));
const LeadsPage = lazy(() => import('./pages/crm/LeadsPage.js').then((m) => ({ default: m.LeadsPage })));
const ClientsPage = lazy(() => import('./pages/crm/ClientsPage.js').then((m) => ({ default: m.ClientsPage })));
const QuotationsPage = lazy(() => import('./pages/crm/QuotationsPage.js').then((m) => ({ default: m.QuotationsPage })));
const MeetingsPage = lazy(() => import('./pages/crm/MeetingsPage.js').then((m) => ({ default: m.MeetingsPage })));
const ServicesPage = lazy(() => import('./pages/crm/ServicesPage.js').then((m) => ({ default: m.ServicesPage })));

const ProjectsDashboardPage = lazy(() => import('./pages/projects/ProjectsDashboardPage.js').then((m) => ({ default: m.ProjectsDashboardPage })));
const ProjectsListPage = lazy(() => import('./pages/projects/ProjectsListPage.js').then((m) => ({ default: m.ProjectsListPage })));
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage.js').then((m) => ({ default: m.ProjectDetailPage })));
const DeliverablesPage = lazy(() => import('./pages/projects/DeliverablesPage.js').then((m) => ({ default: m.DeliverablesPage })));
const ResourceBookingPage = lazy(() => import('./pages/projects/ResourceBookingPage.js').then((m) => ({ default: m.ResourceBookingPage })));

const PageLoaderFallback: React.FC = () => (
  <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
    <Skeleton height="32px" width="300px" />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)' }}>
      <Skeleton height="100px" />
      <Skeleton height="100px" />
      <Skeleton height="100px" />
      <Skeleton height="100px" />
    </div>
    <Skeleton height="300px" />
  </div>
);

const ProtectedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  if (loading) {
    return <BrandedLoader message="Loading Drishti Business OS..." fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <NotificationProvider>
      <ErrorBoundary>
        <div className="app-layout">
          <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)} />
          <div className="main-wrapper">
            <Header onOpenSearch={() => setIsSearchOpen(true)} />
            <main className="main-content">
              <Suspense fallback={<PageLoaderFallback />}>{children}</Suspense>
            </main>
          </div>

          {/* Global Search & Notifications */}
          <UniversalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
          <NotificationDrawer />
        </div>
      </ErrorBoundary>
    </NotificationProvider>
  );
};

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <Suspense fallback={<PageLoaderFallback />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedLayout>
                  <DashboardPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedLayout>
                  <UsersPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedLayout>
                  <RolesPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/departments"
              element={
                <ProtectedLayout>
                  <DepartmentsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedLayout>
                  <SettingsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/audit"
              element={
                <ProtectedLayout>
                  <AuditLogsPage />
                </ProtectedLayout>
              }
            />

            {/* PHASE 2: FINANCE ROUTES */}
            <Route
              path="/finance/dashboard"
              element={
                <ProtectedLayout>
                  <FinanceDashboardPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/coa"
              element={
                <ProtectedLayout>
                  <ChartOfAccountsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/transactions"
              element={
                <ProtectedLayout>
                  <TransactionsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/invoices"
              element={
                <ProtectedLayout>
                  <InvoicesPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/expenses"
              element={
                <ProtectedLayout>
                  <ExpensesPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/budgets"
              element={
                <ProtectedLayout>
                  <BudgetsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/investments"
              element={
                <ProtectedLayout>
                  <OwnerInvestmentsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/finance/reports"
              element={
                <ProtectedLayout>
                  <FinancialReportsPage />
                </ProtectedLayout>
              }
            />

            {/* PHASE 3: HRMS & TEAM MANAGEMENT ROUTES */}
            <Route
              path="/hrms/dashboard"
              element={
                <ProtectedLayout>
                  <HrDashboardPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/hrms/attendance"
              element={
                <ProtectedLayout>
                  <AttendancePage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/hrms/leave"
              element={
                <ProtectedLayout>
                  <LeaveManagementPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/hrms/daily-reports"
              element={
                <ProtectedLayout>
                  <DailyReportsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/hrms/tasks"
              element={
                <ProtectedLayout>
                  <TaskManagementPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/hrms/performance"
              element={
                <ProtectedLayout>
                  <PerformancePage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/hrms/announcements"
              element={
                <ProtectedLayout>
                  <AnnouncementsPage />
                </ProtectedLayout>
              }
            />

            {/* PHASE 4: CRM & CLIENT MANAGEMENT ROUTES */}
            <Route
              path="/crm/dashboard"
              element={
                <ProtectedLayout>
                  <CrmDashboardPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/crm/leads"
              element={
                <ProtectedLayout>
                  <LeadsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/crm/clients"
              element={
                <ProtectedLayout>
                  <ClientsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/crm/quotations"
              element={
                <ProtectedLayout>
                  <QuotationsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/crm/meetings"
              element={
                <ProtectedLayout>
                  <MeetingsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/crm/services"
              element={
                <ProtectedLayout>
                  <ServicesPage />
                </ProtectedLayout>
              }
            />

            {/* PHASE 5: PROJECT & OPERATIONS ROUTES */}
            <Route
              path="/projects/dashboard"
              element={
                <ProtectedLayout>
                  <ProjectsDashboardPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/projects/list"
              element={
                <ProtectedLayout>
                  <ProjectsListPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/projects/detail/:id"
              element={
                <ProtectedLayout>
                  <ProjectDetailPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/projects/deliverables"
              element={
                <ProtectedLayout>
                  <DeliverablesPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/projects/resources"
              element={
                <ProtectedLayout>
                  <ResourceBookingPage />
                </ProtectedLayout>
              }
            />

            <Route
              path="/403"
              element={
                <ProtectedLayout>
                  <ForbiddenPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/404"
              element={
                <ProtectedLayout>
                  <NotFoundPage />
                </ProtectedLayout>
              }
            />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
